import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';
import Stack from '@mui/material/Stack';
import { CircleFlag } from 'react-circle-flags';
import { useSelector, useDispatch } from 'react-redux';
import {
  Skeleton,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  activeCountriesSelector,
  masterDataSelector,
  dashboardConciliationsStateSelector,
  dashboardCountrySelectedSelector,
  changeDashboardCountrySelected,
} from '../../../state';
import { ConciliationStatus } from '../../../types';
import { getPercent, getPercentColor } from '../../../helpers';

const Row = ({
  country,
  closuresReceived,
  conciliationOk,
  index,
  theme,
  selected,
}: Pick<ConciliationStatus, 'country' | 'closuresReceived' | 'conciliationOk'> & {
  index: number;
  theme: Theme;
  selected: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const percentage = getPercent(conciliationOk, closuresReceived);
  const percentColor = getPercentColor(theme, percentage);

  const handleClick = () => {
    dispatch(changeDashboardCountrySelected(country));
    console.log(`Selected country: ${country}`);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTableRowElement> = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <TableRow
      hover
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      selected={selected}
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        backgroundColor: theme.palette.background.paper,
        '&&:hover': {
          backgroundColor: selected ? theme.palette.action.selected : theme.palette.action.focus,
        },
      }}
    >
      <TableCell component="th" scope="row">
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircleFlag countryCode={(country || '').toLowerCase()} height="20" />
          <Box>{t(`operativeStructure.countries.${country}`)}</Box>
        </Stack>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body1" fontWeight="bold" color={percentColor}>
          {conciliationOk} / {closuresReceived}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body1" fontWeight="bold" color={percentColor}>
          {conciliationOk} ({percentage}%)
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const IncorporationError = () => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" alignItems="center" color="error" padding={1} spacing={1}>
      <ErrorIcon color="error" />
      <Typography variant="body1" color="error">
        {t('errors.generics.loadingData')}
      </Typography>
    </Stack>
  );
};

const Content = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    data,
    loading: resourceLoading,
    error,
  } = useSelector(dashboardConciliationsStateSelector);
  const masterDataResource = useSelector(masterDataSelector);
  const activeCountriesResource = useSelector(activeCountriesSelector);
  const selectedCountry = useSelector(dashboardCountrySelectedSelector);

  const loading = resourceLoading || masterDataResource.loading || activeCountriesResource.loading;

  const sortedData = useMemo<ConciliationStatus[] | undefined>(
    () =>
      data
        ?.slice()
        .sort((a: ConciliationStatus, b: ConciliationStatus) => a.country.localeCompare(b.country)),
    [data]
  );

  return (
    <Box>
      <TableContainer data-guide="country-conciliation-table">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('dashboard.conciliations-state.country')}</TableCell>
              <TableCell align="center">
                {t('dashboard.conciliations-state.closures-received')}
              </TableCell>
              <TableCell align="center">
                {t('dashboard.conciliations-state.conciliated-ok')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!error &&
              !loading &&
              sortedData?.map((i, index) => (
                <Row
                  key={`${i.country}-${i.startDate?.toString()}-${i.endDate?.toString()}`}
                  country={i.country}
                  closuresReceived={i.closuresReceived}
                  conciliationOk={i.conciliationOk}
                  index={index}
                  theme={theme}
                  selected={i.country === selectedCountry}
                />
              ))}
            {loading && (
              <>
                <TableRow>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              </>
            )}
            {!loading && error && (
              <TableRow>
                <TableCell colSpan={3}>
                  <IncorporationError />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Content;
